import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { taskAPI, branchAPI, cloudinaryService } from '../../services';
import { toast } from 'react-toastify';
import CriterionInputComponent from '../fields/CriterionInputComponent';
import SampleEvidencePickerModal from './SampleEvidencePickerModal';

export default function TaskFormModal({ show, onHide, task, onSave }) {
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [uploadingSample, setUploadingSample] = useState(false);
  const [sampleUrlInput, setSampleUrlInput] = useState('');
  // #200056 — gợi ý tiêu chí đã từng dùng + bảng chọn lại ảnh mẫu cũ, đặt tại Cấu hình Task (admin).
  const [criteriaSuggestions, setCriteriaSuggestions] = useState([]);
  const [samplePickerOpen, setSamplePickerOpen] = useState(false);
  // #205306 — vị trí mẫu đang được THAY ảnh (null = bảng đang mở ở chế độ thêm mẫu mới).
  const [replacingSampleIndex, setReplacingSampleIndex] = useState(null);

  const isVideoUrl = (url) =>
    /\.(mp4|webm|mov|avi|mkv|ogv)(\?|$)/i.test(url) || url.includes('/video/upload/');

  // sample_evidence_config: [{ url: string, criteria: [{ id: string, name: string }] }]
  const [sampleEvidenceConfig, setSampleEvidenceConfig] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    category: 'GENERAL',
    task_type: 'SUB_TASK',
    has_points: false,
    points: 0,
    estimated_minutes: '',
    deadline_minutes_after_shift_start: '',
    shift_segment: '',
    dual_review: false,
    branch_id: null,
    is_repeatable: true,
    requires_review: false,
    cross_review_previous_shift: false,
    cross_review_same_shift: false,
    requires_evidence: false,
    qc_type: 'IMAGE',
    work_content: '',
    checklist_items: [],
    sample_evidence_urls: [],
    is_active: true,
    notify_telegram: false,
    required_for_checkout: false,
    is_urgent: false,
    is_training_default: false,
    days_of_week: [],
  });

  useEffect(() => {
    if (show) {
      loadBranches();
      if (task) {
        // Build sampleEvidenceConfig: dùng sample_evidence_config nếu có, fallback từ sample_evidence_urls
        const existingConfig = task.sample_evidence_config;
        const builtConfig = Array.isArray(existingConfig) && existingConfig.length > 0
          ? existingConfig
          : (task.sample_evidence_urls || []).map((url) => ({ url, criteria: [] }));
        setSampleEvidenceConfig(builtConfig);

        setFormData({
          name: task.name || '',
          category: task.category || 'GENERAL',
          task_type: task.task_type || 'SUB_TASK',
          has_points: task.has_points || false,
          points: task.points || 0,
          estimated_minutes: task.estimated_minutes != null ? String(task.estimated_minutes) : '',
          deadline_minutes_after_shift_start: task.deadline_minutes_after_shift_start != null ? String(task.deadline_minutes_after_shift_start) : '',
          shift_segment: task.shift_segment || '',
          dual_review: task.dual_review || false,
          branch_id: task.branch_id || null,
          is_repeatable: task.is_repeatable !== undefined ? task.is_repeatable : true,
          requires_review: task.requires_review || false,
          cross_review_previous_shift: task.cross_review_previous_shift || false,
          cross_review_same_shift: task.cross_review_same_shift || false,
          requires_evidence: task.requires_evidence || false,
          qc_type: task.qc_type || 'IMAGE',
          work_content: task.work_content || '',
          checklist_items: task.checklist_items || [],
          sample_evidence_urls: task.sample_evidence_urls || [],
          is_active: task.is_active !== undefined ? task.is_active : true,
          notify_telegram: task.notify_telegram || false,
          required_for_checkout: task.required_for_checkout || false,
          is_urgent: task.is_urgent || false,
          is_training_default: task.is_training_default || false,
          days_of_week: task.days_of_week || [],
        });
      } else {
        setSampleEvidenceConfig([]);
        setFormData({
          name: '',
          category: 'GENERAL',
          task_type: 'SUB_TASK',
          has_points: false,
          points: 0,
          estimated_minutes: '',
          deadline_minutes_after_shift_start: '',
          shift_segment: '',
          dual_review: false,
          branch_id: null,
          is_repeatable: true,
          requires_review: false,
          cross_review_previous_shift: false,
          cross_review_same_shift: false,
          requires_evidence: false,
          qc_type: 'IMAGE',
          work_content: '',
          checklist_items: [],
          sample_evidence_urls: [],
          is_active: true,
          notify_telegram: false,
          required_for_checkout: false,
          is_urgent: false,
          is_training_default: false,
          days_of_week: [],
        });
      }
    }
  }, [show, task]);

  // #200056 — tải 1 lần danh sách tiêu chí đã dùng rồi lọc tại chỗ khi gõ (danh sách ngắn).
  // Gọi hỏng thì im lặng bỏ qua — ô nhập vẫn gõ tay bình thường, không chặn việc tạo/sửa task.
  useEffect(() => {
    if (!show) return undefined;
    let huy = false;
    taskAPI
      .getCriteriaSuggestions({ limit: 100 })
      .then((res) => {
        if (huy) return;
        const items = res?.data ?? (Array.isArray(res) ? res : []);
        setCriteriaSuggestions(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
    return () => {
      huy = true;
    };
  }, [show]);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.getBranches({ is_active: true, page: 1, limit: 100 });
      setBranches(res.data || res);
    } catch (err) {
      console.error('Error loading branches:', err);
    }
  };

  const handleUploadSampleEvidence = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploadingSample(true);
      const result = await cloudinaryService.uploadImage(file, { folder: 'tasks/sample_evidence' });
      if (result?.secure_url) {
        setSampleEvidenceConfig((prev) => [...prev, { url: result.secure_url, criteria: [] }]);
      }
    } catch (err) {
      console.error('Error upload sample evidence:', err);
      toast.error('Không thể upload mẫu bằng chứng');
    } finally {
      setUploadingSample(false);
      event.target.value = '';
    }
  };

  const handleAddSampleUrl = () => {
    const url = sampleUrlInput.trim();
    if (!url) return;
    setSampleEvidenceConfig((prev) => [...prev, { url, criteria: [] }]);
    setSampleUrlInput('');
  };

  /**
   * #200056 — thêm 1 ảnh mẫu cũ vào task đang soạn, CHÉP nguyên tiêu chí của nó. Mỗi tiêu chí
   * sinh mã MỚI: task mới và task gốc độc lập hoàn toàn, sửa/xoá bên nào cũng không đụng bên kia.
   * Ảnh đã có sẵn trong task thì bỏ qua, không thêm trùng.
   * #181106 — dùng chung cho cả ảnh lấy từ "Kho ảnh"; ảnh nhân viên đã up về đây với tiêu chí rỗng.
   */
  const handlePickSampleFromOld = (item) => {
    const url = (item?.url || '').trim();
    if (!url) return;
    // #205306 — đang THAY ảnh của 1 mẫu có sẵn: chỉ đổi URL, GIỮ NGUYÊN tiêu chí QC của mẫu.
    if (replacingSampleIndex !== null) {
      setSampleEvidenceConfig((prev) =>
        prev.map((s, i) => (i === replacingSampleIndex ? { ...s, url } : s)),
      );
      setReplacingSampleIndex(null);
      setSamplePickerOpen(false);
      return;
    }
    if (sampleEvidenceConfig.some((s) => s.url === url)) return;
    const criteria = (Array.isArray(item?.criteria) ? item.criteria : [])
      .map((c) => (typeof c?.name === 'string' ? c.name.trim() : ''))
      .filter(Boolean)
      .map((name) => ({
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random()}`,
        name,
      }));
    setSampleEvidenceConfig((prev) => [...prev, { url, criteria }]);
    setSamplePickerOpen(false);
  };

  const handleRemoveSample = (idx) => {
    setSampleEvidenceConfig((prev) => prev.filter((_, i) => i !== idx));
  };

  // #205306 — mở bảng chọn ảnh nhân viên để THAY ảnh cho đúng mẫu đang có (giữ nguyên tiêu chí).
  const handleStartReplaceSample = (idx) => {
    setReplacingSampleIndex(idx);
    setSamplePickerOpen(true);
  };

  const handleAddCriteria = (sampleIdx) => {
    setSampleEvidenceConfig((prev) =>
      prev.map((s, i) =>
        i === sampleIdx
          ? { ...s, criteria: [...s.criteria, { id: crypto.randomUUID(), name: '' }] }
          : s,
      ),
    );
  };

  const handleUpdateCriteriaName = (sampleIdx, criteriaIdx, name) => {
    setSampleEvidenceConfig((prev) =>
      prev.map((s, i) =>
        i === sampleIdx
          ? {
              ...s,
              criteria: s.criteria.map((c, ci) =>
                ci === criteriaIdx ? { ...c, name } : c,
              ),
            }
          : s,
      ),
    );
  };

  const handleRemoveCriteria = (sampleIdx, criteriaIdx) => {
    setSampleEvidenceConfig((prev) =>
      prev.map((s, i) =>
        i === sampleIdx
          ? { ...s, criteria: s.criteria.filter((_, ci) => ci !== criteriaIdx) }
          : s,
      ),
    );
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.category.trim()) {
      toast.error('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }

    try {
      setLoading(true);
      const taskData = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        task_type: formData.task_type || 'SUB_TASK',
        has_points: formData.has_points,
        points: formData.points,
        estimated_minutes: formData.estimated_minutes ? parseInt(formData.estimated_minutes, 10) : null,
        deadline_minutes_after_shift_start: formData.deadline_minutes_after_shift_start ? parseInt(formData.deadline_minutes_after_shift_start, 10) : null,
        // #142526 — khung giờ trong ca; rỗng = không ràng buộc khung giờ
        // #213032 — bật nghiệm thu 2 lần thì khung giờ đơn bị bỏ qua (2 lượt đã cố định Đầu + Cuối ca)
        shift_segment: formData.dual_review ? null : (formData.shift_segment || null),
        dual_review: formData.dual_review,
        branch_id: formData.branch_id || null,
        is_repeatable: formData.is_repeatable,
        requires_review: formData.requires_review,
        cross_review_previous_shift: formData.cross_review_previous_shift,
        cross_review_same_shift: formData.cross_review_same_shift,
        requires_evidence: formData.requires_evidence,
        qc_type: formData.qc_type || 'IMAGE',
        work_content: formData.work_content || '',
        checklist_items: formData.checklist_items.filter((item) => item.trim() !== ''),
        sample_evidence_config: sampleEvidenceConfig.filter((s) => s.url.trim() !== ''),
        sample_evidence_urls: sampleEvidenceConfig.map((s) => s.url).filter(Boolean),
        is_active: formData.is_active,
        notify_telegram: formData.notify_telegram,
        required_for_checkout: formData.required_for_checkout,
        is_urgent: formData.is_urgent,
        is_training_default: formData.is_training_default,
        days_of_week: formData.days_of_week || [],
      };

      let result;
      if (task?.id) {
        result = await taskAPI.updateTask(task.id, taskData);
        toast.success('Cập nhật task thành công!');
      } else {
        result = await taskAPI.createTask(taskData);
        toast.success('Tạo task mới thành công!');
      }

      onSave?.(result.data || result);
      onHide();
    } catch (err) {
      console.error('Error saving task:', err);
      toast.error(task?.id ? 'Không thể cập nhật task' : 'Không thể tạo task mới');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{task ? 'Chỉnh sửa task' : 'Tạo task mới'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Tên *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="tc-ten-task"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Danh mục *</Form.Label>
              <Form.Control
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Loại công việc *</Form.Label>
              <Form.Select
                value={formData.task_type || 'SUB_TASK'}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
              >
                <option value="SUB_TASK">Việc phải nghiệm thu (SUB_TASK)</option>
                <option value="MAIN_TASK">Việc trực ca (MAIN_TASK)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Việc trực ca: làm lặp lại theo tình huống suốt ca, không bấm hoàn thành từng lần. Việc phải nghiệm thu: làm đúng 1 lần trong ca, phải bấm hoàn thành (hoặc chụp ảnh chứng minh). Cả hai đều bắt buộc.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Thời lượng ước tính (phút)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={480}
                placeholder="VD: 15"
                value={formData.estimated_minutes}
                onChange={(e) => setFormData({ ...formData, estimated_minutes: e.target.value })}
              />
              <Form.Text className="text-muted">
                Dùng để cảnh báo khi 1 ca bị dồn quá nhiều task. Để trống = không tính.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Deadline sau khi ca bắt đầu (phút)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="VD: 60"
                value={formData.deadline_minutes_after_shift_start}
                onChange={(e) => setFormData({ ...formData, deadline_minutes_after_shift_start: e.target.value })}
              />
              <Form.Text className="text-muted">
                Nếu task chưa hoàn thành sau thời gian này (tính từ giờ bắt đầu ca), hệ thống sẽ tự động đánh trượt và trừ 0.5 điểm. Để trống = không áp dụng deadline.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Khung giờ trong ca</Form.Label>
              <Form.Select
                value={formData.dual_review ? '' : (formData.shift_segment || '')}
                onChange={(e) => setFormData({ ...formData, shift_segment: e.target.value })}
                disabled={formData.dual_review}
                data-testid="tc-khung-gio"
              >
                <option value="">Không ràng buộc khung giờ</option>
                <option value="START">Đầu ca</option>
                <option value="MIDDLE">Giữa ca</option>
                <option value="END">Cuối ca</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {formData.dual_review
                  ? 'Đang bật "Nghiệm thu 2 lần" nên khung giờ đơn không dùng — hệ thống tự tạo 2 lượt Đầu ca và Cuối ca.'
                  : 'Ca được chia làm 3 phần bằng nhau. Nhân viên thấy khung giờ này ngay trên việc, và nếu hết khung giờ mà chưa làm xong thì được nhắc nhở qua Telegram (chỉ nhắc, không chặn, không trừ điểm). Để trống = không nhắc.'}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="dual_review"
                label="Nghiệm thu 2 lần: Đầu ca + Cuối ca"
                checked={formData.dual_review}
                disabled={formData.task_type === 'MAIN_TASK'}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    dual_review: on,
                    // Bật 2 lần thì khung giờ đơn bị bỏ qua (2 lượt đã cố định Đầu + Cuối ca)
                    shift_segment: on ? '' : prev.shift_segment,
                  }));
                }}
                data-testid="tc-nghiem-thu-2-lan"
              />
              <Form.Text className="text-muted">
                {formData.task_type === 'MAIN_TASK'
                  ? 'Việc trực ca không nghiệm thu nên không áp dụng.'
                  : 'Bật: mỗi ca sinh 2 lượt của việc này — lượt Đầu ca và lượt Cuối ca, mỗi lượt có ảnh bằng chứng, nghiệm thu, chấm QC và điểm thưởng/phạt riêng. Mặc định tắt = làm 1 lần mỗi ca như hiện nay.'}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Chi nhánh</Form.Label>
              <Form.Select
                value={formData.branch_id || ''}
                onChange={(e) => setFormData({ ...formData, branch_id: e.target.value || null })}
                data-testid="tc-chi-nhanh"
              >
                <option value="">Tất cả chi nhánh</option>
                {branches.map((branch) => (
                  <option
                    key={branch.id}
                    value={branch.id}
                  >
                    {branch.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Trạng thái</Form.Label>
              <Form.Select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <option value={true}>Hoạt động</option>
                <option value={false}>Tạm dừng</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Nội dung công việc</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.work_content}
                onChange={(e) => setFormData({ ...formData, work_content: e.target.value })}
                placeholder="Mô tả chi tiết nội dung công việc cần thực hiện..."
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Checklist (mỗi mục một dòng)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.checklist_items.join('\n')}
                onChange={(e) => {
                  const items = e.target.value.split('\n');
                  setFormData({ ...formData, checklist_items: items });
                }}
                onBlur={(e) => {
                  const items = e.target.value.split('\n').filter((item) => item.trim() !== '');
                  setFormData({ ...formData, checklist_items: items });
                }}
                placeholder="Dọn quầy&#10;Đếm tiền&#10;Khóa cửa"
              />
              <Form.Text className="text-muted">Nhập mỗi mục checklist trên một dòng riêng</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <hr />

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="has_points"
                label={
                  <>
                    Bật tính điểm
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Khi bật, điểm dương (+) là thưởng, điểm âm (-) là phạt. Khi review PASS: cộng điểm cho người
                          làm. Khi review FAIL: nếu điểm dương → thưởng reviewer, nếu điểm âm → trừ điểm người làm.
                        </Tooltip>
                      }
                    >
                      <i
                        className="material-icons ms-1"
                        style={{ fontSize: '16px', cursor: 'help' }}
                      >
                        info
                      </i>
                    </OverlayTrigger>
                  </>
                }
                checked={formData.has_points}
                onChange={(e) => setFormData({ ...formData, has_points: e.target.checked })}
              />
            </Form.Group>
          </Col>
        </Row>

        {formData.has_points && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Điểm</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value || '0') })}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <hr />

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="cross_review_previous_shift"
                label="Giám sát chéo ca trước"
                checked={formData.cross_review_previous_shift}
                onChange={(e) => setFormData({ ...formData, cross_review_previous_shift: e.target.checked })}
              />
              <Form.Text className="text-muted">Yêu cầu ca sau review task này</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="cross_review_same_shift"
                label="Giám sát chéo cùng ca"
                checked={formData.cross_review_same_shift}
                onChange={(e) => setFormData({ ...formData, cross_review_same_shift: e.target.checked })}
              />
              <Form.Text className="text-muted">Yêu cầu nhân sự cùng ca review</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="requires_evidence"
                label="Bắt buộc bằng chứng ảnh"
                checked={formData.requires_evidence}
                onChange={(e) => setFormData({ ...formData, requires_evidence: e.target.checked })}
              />
              <Form.Text className="text-muted">Bắt buộc upload ảnh khi hoàn thành task</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="is_repeatable"
                label="Có thể lặp lại"
                checked={formData.is_repeatable}
                onChange={(e) => setFormData({ ...formData, is_repeatable: e.target.checked })}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_urgent"
                label={
                  <span>
                    <span
                      className="badge bg-danger me-2"
                      style={{ fontSize: '11px' }}
                    >
                      KHẨN CẤP
                    </span>
                    Task khẩn cấp / đột xuất
                  </span>
                }
                checked={formData.is_urgent}
                onChange={(e) => {
                  const urgent = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    is_urgent: urgent,
                    is_repeatable: urgent ? false : prev.is_repeatable,
                  }));
                }}
              />
              <Form.Text className="text-muted">
                {formData.is_urgent
                  ? 'Task đột xuất — tự động tắt "Có thể lặp lại". Hiển thị badge đỏ trong danh sách.'
                  : 'Đánh dấu nếu đây là task phát sinh ngoài quy trình thông thường'}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="requires_review"
                label="Cần review"
                checked={formData.requires_review}
                onChange={(e) => setFormData({ ...formData, requires_review: e.target.checked })}
              />
              <Form.Text className="text-muted">Task cần QC review</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Loại QC</Form.Label>
              <Form.Select
                value={formData.qc_type || 'IMAGE'}
                onChange={(e) => setFormData({ ...formData, qc_type: e.target.value })}
              >
                <option value="IMAGE">IMAGE</option>
                <option value="CAMERA">CAMERA</option>
                <option value="FORM_DOCS">FORM_DOCS</option>
              </Form.Select>
              <Form.Text className="text-muted">Loại bằng chứng QC yêu cầu</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <hr />

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="notify_telegram"
                label="Thông báo Telegram Admin"
                checked={formData.notify_telegram}
                onChange={(e) => setFormData({ ...formData, notify_telegram: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Thông báo group Telegram admin khi hoàn thành + cảnh báo nếu chưa xong khi ca còn 30 phút
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="required_for_checkout"
                label="Bắt buộc hoàn thành mới check-out"
                checked={formData.required_for_checkout}
                onChange={(e) => setFormData({ ...formData, required_for_checkout: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Nhân viên phải hoàn thành task này (không được bỏ qua) mới được check-out ca
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="is_training_default"
                label={
                  <span>
                    <span className="badge bg-info me-2" style={{ fontSize: '11px' }}>TRAINING</span>
                    Task mặc định cho ca training
                  </span>
                }
                checked={formData.is_training_default}
                onChange={(e) => setFormData({ ...formData, is_training_default: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Tự động gán cho mọi ca training của nhân viên mới khi check-in. Chỉ có ý nghĩa khi category là TRAINING.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Ngày trong tuần áp dụng</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {[
                  { label: 'CN', value: 0 },
                  { label: 'T2', value: 1 },
                  { label: 'T3', value: 2 },
                  { label: 'T4', value: 3 },
                  { label: 'T5', value: 4 },
                  { label: 'T6', value: 5 },
                  { label: 'T7', value: 6 },
                ].map(({ label, value }) => {
                  const selected = (formData.days_of_week || []).includes(value);
                  return (
                    <Button
                      key={value}
                      variant={selected ? 'primary' : 'outline-secondary'}
                      size="sm"
                      style={{ minWidth: 40 }}
                      onClick={() => {
                        const current = formData.days_of_week || [];
                        setFormData({
                          ...formData,
                          days_of_week: selected
                            ? current.filter((d) => d !== value)
                            : [...current, value].sort((a, b) => a - b),
                        });
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
              <Form.Text className="text-muted">
                Không chọn = áp dụng tất cả các ngày (mặc định)
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Mẫu bằng chứng &amp; Tiêu chí QC</Form.Label>
              <div className="d-flex flex-column gap-3 mb-2">
                {sampleEvidenceConfig.map((sample, idx) => (
                  <div key={idx} style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 12 }}>
                    <div className="d-flex align-items-start gap-2 mb-2">
                      {isVideoUrl(sample.url) ? (
                        <video
                          src={sample.url}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={sample.url}
                          alt={`sample-${idx}`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted fw-bold">Mẫu {idx + 1}</small>
                          <div className="d-flex gap-1">
                            {/* #205306 — thay ảnh mẫu bằng ảnh nhân viên đã chụp cho việc này, giữ tiêu chí. */}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleStartReplaceSample(idx)}
                              data-testid={`tc-thay-anh-mau-${idx}`}
                            >
                              <i className="material-icons align-middle" style={{ fontSize: 16 }}>
                                swap_horiz
                              </i>{' '}
                              Thay ảnh
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveSample(idx)}
                            >
                              Xoá mẫu
                            </Button>
                          </div>
                        </div>
                        <div>
                          <small className="text-muted d-block mb-1">Tiêu chí QC:</small>
                          {sample.criteria.map((c, ci) => (
                            <div key={ci} className="d-flex gap-1 mb-1">
                              <CriterionInputComponent
                                value={c.name}
                                onChange={(name) => handleUpdateCriteriaName(idx, ci, name)}
                                suggestions={criteriaSuggestions}
                                placeholder={`Tiêu chí ${ci + 1} — gõ để xem gợi ý`}
                                testId={`tc-tieu-chi-${idx}-${ci}`}
                              />
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleRemoveCriteria(idx, ci)}
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleAddCriteria(idx)}
                          >
                            + Thêm tiêu chí
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex flex-column gap-2">
                <Form.Control
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleUploadSampleEvidence}
                  disabled={uploadingSample}
                  data-testid="tc-file-mau"
                />
                <div className="d-flex gap-2">
                  <Form.Control
                    type="url"
                    placeholder="Dán URL mẫu (ảnh/video) rồi nhấn Thêm"
                    value={sampleUrlInput}
                    onChange={(e) => setSampleUrlInput(e.target.value)}
                    data-testid="tc-url-mau"
                  />
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={handleAddSampleUrl}
                    disabled={!sampleUrlInput.trim()}
                    data-testid="tc-them-url"
                  >
                    Thêm
                  </Button>
                </div>
                {/* #200056 + #181106 — chọn ảnh có sẵn: mẫu đã dùng (kèm tiêu chí) hoặc ảnh nhân viên đã up. */}
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={() => {
                    setReplacingSampleIndex(null);
                    setSamplePickerOpen(true);
                  }}
                  data-testid="tc-mo-chon-anh-cu"
                >
                  Chọn ảnh có sẵn
                </Button>
              </div>
              <Form.Text className="text-muted">
                Mỗi mẫu có thể thêm nhiều tiêu chí — QC staff sẽ tick từng tiêu chí khi review.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
        >
          Hủy
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
          data-testid="tc-luu-task"
        >
          {loading ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </Modal.Footer>

      {/* #200056 + #181106 — bảng chọn ảnh có sẵn (tab ảnh mẫu cũ / tab kho ảnh), mở từ nút ở trên.
          #205306 — mở ở chế độ 'replace' khi bấm "Thay ảnh" trên 1 mẫu: chỉ ảnh nhân viên của việc
          này, đổi URL giữ nguyên tiêu chí. */}
      <SampleEvidencePickerModal
        show={samplePickerOpen}
        onHide={() => {
          setSamplePickerOpen(false);
          setReplacingSampleIndex(null);
        }}
        branches={branches}
        defaultBranchId={formData.branch_id}
        mode={replacingSampleIndex !== null ? 'replace' : 'add'}
        taskId={task?.id || null}
        existingUrls={sampleEvidenceConfig.map((s) => s.url)}
        onPick={handlePickSampleFromOld}
      />
    </Modal>
  );
}
